/**
 * 🐞 IssueListView — /issues  (로드맵 4: 도메인 스윔레인 칸반)
 *
 * 행=도메인, 열=상태(대기/진행중/수정됨/닫힘). 카드를 드래그해 상태 전이 —
 * 옮길 수 있는 열만 하이라이트(허용 전이만), 닫힘으로 놓으면 종류(resolution) 선택,
 * 전이 후 되돌리기 토스트. 카드 클릭 → 우측 상세 드로어(3층: 기획자·개발자·제보/이력).
 *
 * 데이터: GET /api/admin/issue. 필터(도메인/심각도/상태/태그/내제보)는 클라이언트.
 * is_mine·domain·tags 는 서버가 행마다 내려줌. 도메인 라벨은 lib/issue-taxonomy.
 */

import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';
import * as api from '../api/client';
import { escapeHtml, fmtDate, fmtDateTime } from '../lib/cells';
import { getCurrentRole } from '../lib/auth';
import { domainLabel, subLabel, domainSortKey } from '../lib/issue-taxonomy';

const STATUS_LABEL: Record<api.IssueStatus, string> = {
  open: '대기', in_progress: '진행중', resolved: '완료', closed: '닫힘',
};
const STATUS_LIST: api.IssueStatus[] = ['open', 'in_progress', 'resolved', 'closed'];
const STATUS_COLOR: Record<string, string> = {
  open: '#2563eb', in_progress: '#7c3aed', resolved: '#059669', closed: '#94a0b5',
};
const SEVERITY_LIST = ['긴급', '높음', '보통', '낮음'];
const SEVERITY_COLOR: Record<string, string> = {
  긴급: '#dc2626', 높음: '#ea580c', 보통: '#ca8a04', 낮음: '#8a93a6',
};
// 프런트 하이라이트용 허용 전이(백엔드 ALLOWED_TRANSITIONS 와 동일해야 함).
const ALLOWED: Record<api.IssueStatus, api.IssueStatus[]> = {
  open:        ['in_progress', 'closed'],
  in_progress: ['resolved', 'closed', 'open'],
  resolved:    ['in_progress', 'closed'],
  closed:      ['open'],
};

export interface IssueListLoaded {
  rows: api.IssueListRow[];
  isDeveloper: boolean;
}

interface IssueListData {
  rows:          api.IssueListRow[];
  fDomain:       string;
  fSeverity:     string;
  fStatus:       string;
  fTag:          string;
  fMine:         boolean;
  view:          'board' | 'purpose';
  hideDone:      boolean;
  collapsed:     string[];
  // 상세 드로어
  drawerId:      number | null;
  detail:        Record<number, api.IssueDetail>;
  loadingDetail: number | null;
  detailError:   string;
  // 전이
  closeId:         number | null;   // resolution 모달 대상
  closeResolution: string;
  closeNote:       string;
  transitioning:   boolean;
  actionError:     string;
  enrichingId:     number | null;
  lastUndo:        { id: number; from: api.IssueStatus } | null;
  isDeveloper:     boolean;
}

export class IssueListView extends SolaView<IssueListData> {
  private toastTimer: ReturnType<typeof setTimeout> | null = null;
  private dragId: number | null = null;
  private dragMoved = false;
  // 열린 상태/심각도 메뉴를 바깥 클릭으로 닫기(문서 레벨).
  private onDocClick = (e: MouseEvent): void => {
    const t = e.target as Node;
    this._rootElement?.querySelectorAll('.st-menu.show, .sev-menu.show').forEach((menu) => {
      const pick = menu.parentElement;
      if (pick && !pick.contains(t)) menu.classList.remove('show');
    });
  };

  override events = {
    click: {
      '@toggle-mine':    'handleToggleMine',
      '@view-board':     'handleViewBoard',
      '@view-purpose':   'handleViewPurpose',
      '@hide-done':      'handleHideDone',
      '@lane-toggle':    'handleLaneToggle',
      '@due-clear':      'handleDueClear',
      '@card':           'handleCardClick',
      '@drawer-close':   'handleDrawerClose',
      '@enrich':         'handleEnrich',
      '@pick-open':      'handlePickOpen',
      '@pick-to':        'handlePickTo',
      '@sev-open':       'handleSevOpen',
      '@sev-to':         'handleSevTo',
      '@res-submit':     'handleResSubmit',
      '@res-cancel':     'handleResCancel',
      '@undo':           'handleUndo',
      '@toast-close':    'handleToastClose',
      '@overlay-click':  'handleOverlayClick',
    },
    change: {
      '@f-domain':       'handleFDomain',
      '@f-severity':     'handleFSeverity',
      '@f-status':       'handleFStatus',
      '@f-tag':          'handleFTag',
      '@due-set':        'handleDueSet',
      '@res-pick':       'handleResPick',
    },
    input: {
      '@res-note':       'handleResNote',
    },
  };

  constructor(loaded: IssueListLoaded) {
    super();
    this.data = {
      rows:          loaded.rows ?? [],
      fDomain: '', fSeverity: '', fStatus: '', fTag: '', fMine: false, view: 'board', hideDone: false, collapsed: [],
      drawerId: null, detail: {}, loadingDetail: null, detailError: '',
      closeId: null, closeResolution: '', closeNote: '',
      transitioning: false, actionError: '', enrichingId: null,
      lastUndo: null, isDeveloper: loaded.isDeveloper ?? false,
    };
  }

  override getTitle(): string { return '이슈 — HCLTech 어드민'; }

  override onMount(): void { this.wireDnd(); document.addEventListener('click', this.onDocClick); }

  // ─── 필터 ──────────────────────────────────────────────────────────
  private filtered(): api.IssueListRow[] {
    const d = this.data;
    return d.rows.filter((r) =>
      (!d.fDomain   || (r.domain ?? '') === d.fDomain) &&
      (!d.fSeverity || r.severity === d.fSeverity) &&
      (!d.fStatus   || r.status === d.fStatus) &&
      (!d.fTag      || (r.tags ?? []).includes(d.fTag)) &&
      (!d.fMine     || r.is_mine),
    );
  }
  private allTags(): string[] {
    const set = new Set<string>();
    for (const r of this.data.rows) for (const t of (r.tags ?? [])) set.add(t);
    return [...set].sort();
  }
  private domainKeys(): string[] {
    const set = new Set<string>();
    for (const r of this.data.rows) set.add(r.domain ?? '');
    return [...set].sort((a, b) => domainSortKey(a || null) - domainSortKey(b || null));
  }

  // ─── 렌더 ──────────────────────────────────────────────────────────
  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <div class="page page-issues">
        <header class="page-header"><h1>🐞 이슈 <span class="ph-sub">· 워크플로우</span></h1></header>
        <section class="issue-content">${this.renderContent()}</section>
        ${this.renderOverlays()}
      </div>
    `;
  }

  private renderContent(): string {
    const body = this.data.view === 'board' ? this.renderBoard() : this.renderPurpose();
    return `${this.renderViewToggle()}${this.renderFilters()}${body}`;
  }

  private renderViewToggle(): string {
    const b = this.data.view === 'board';
    const hd = b ? `<button type="button" class="issue-hidedone${this.data.hideDone ? ' on' : ''}" data-action="hide-done">${this.data.hideDone ? '완료·닫힘 보이기' : '완료·닫힘 숨기기'}</button>` : '';
    return `<div class="issue-toolbar">
      <div class="issue-seg">
        <button type="button" data-action="view-board" class="${b ? 'on' : ''}">보드</button>
        <button type="button" data-action="view-purpose" class="${!b ? 'on' : ''}">목적별</button>
      </div>${hd}
    </div>`;
  }

  private renderFilters(): string {
    const opt = (label: string, val: string, cur: string) =>
      `<option value="${escapeHtml(val)}"${val === cur ? ' selected' : ''}>${escapeHtml(label)}</option>`;
    const domOpts = ['<option value="">영역 전체</option>']
      .concat(this.domainKeys().map((k) => opt(domainLabel(k || null), k, this.data.fDomain))).join('');
    const sevOpts = ['<option value="">심각도 전체</option>']
      .concat(SEVERITY_LIST.map((sv) => opt(sv, sv, this.data.fSeverity))).join('');
    const stOpts = ['<option value="">상태 전체</option>']
      .concat(STATUS_LIST.map((sv) => opt(STATUS_LABEL[sv], sv, this.data.fStatus))).join('');
    const tags = this.allTags();
    const tagOpts = ['<option value="">태그 전체</option>']
      .concat(tags.map((t) => opt('#' + t, t, this.data.fTag))).join('');
    return `
      <div class="filter-bar">
        <button class="issue-mine-toggle${this.data.fMine ? ' is-on' : ''}" type="button"
                data-action="toggle-mine" aria-pressed="${this.data.fMine ? 'true' : 'false'}">
          <span>🙋</span> 내 제보</button>
        <select class="filter-select" data-action="f-domain">${domOpts}</select>
        <select class="filter-select" data-action="f-severity">${sevOpts}</select>
        <select class="filter-select" data-action="f-status">${stOpts}</select>
        ${tags.length ? `<select class="filter-select" data-action="f-tag">${tagOpts}</select>` : ''}
      </div>`;
  }

  private renderBoard(): string {
    const rows = this.filtered();
    if (rows.length === 0) {
      return `<div class="kanban"><p class="empty">조건에 맞는 이슈가 없습니다.</p></div>`;
    }
    const cols: api.IssueStatus[] = this.data.hideDone
      ? STATUS_LIST.filter((s) => s === 'open' || s === 'in_progress') : STATUS_LIST;
    const grid = `grid-template-columns:168px repeat(${cols.length},minmax(210px,1fr))`;
    const colhead = cols.map((sv) => {
      const n = rows.filter((r) => r.status === sv).length;
      return `<div class="kb-col"><span class="kb-dot" style="background:${STATUS_COLOR[sv]}"></span>${STATUS_LABEL[sv]}<span class="kb-cnt">${n}</span></div>`;
    }).join('');
    const keys = this.domainKeys().filter((k) => rows.some((r) => (r.domain ?? '') === k));
    const lanes = keys.map((k) => this.renderLane(k, rows.filter((r) => (r.domain ?? '') === k), cols, grid)).join('');
    return `
      <div class="kanban">
        <div class="kb-head" style="${grid}"><div class="kb-corner"></div>${colhead}</div>
        ${lanes}
      </div>
      <p class="list-footer text-muted">총 ${rows.length} 건${this.data.fMine ? ' · 내 제보' : ''}${this.data.hideDone ? ' · 완료·닫힘 숨김' : ''}</p>`;
  }

  private renderLane(domainKey: string, rows: api.IssueListRow[], cols: api.IssueStatus[], grid: string): string {
    const active = rows.filter((r) => r.status === 'open' || r.status === 'in_progress').length;
    const urgent = rows.filter((r) => r.severity === '긴급').length;
    const name = escapeHtml(domainLabel(domainKey || null));
    if (this.data.collapsed.includes(domainKey)) {
      const counts = cols.map((sv) => `${STATUS_LABEL[sv]} ${rows.filter((r) => r.status === sv).length}`).join('  ·  ');
      return `<div class="kb-lane-min" data-action="lane-toggle" data-domain="${escapeHtml(domainKey)}">
        <span class="kb-chev">▸</span><span class="kb-lane-name">${name}</span>
        <span class="kb-badge">${rows.length}</span>
        ${urgent ? `<span class="kb-badge urgent">🔴 ${urgent}</span>` : ''}
        <span class="kb-min-counts">${counts}</span>
      </div>`;
    }
    const cells = cols.map((sv) => {
      const inCell = rows.filter((r) => r.status === sv);
      return `<div class="kb-cell${inCell.length ? '' : ' empty'}" data-domain="${escapeHtml(domainKey)}" data-status="${sv}">${inCell.map((r) => this.renderCard(r)).join('')}</div>`;
    }).join('');
    return `
      <div class="kb-lane" style="${grid}" data-domain="${escapeHtml(domainKey)}">
        <div class="kb-lane-head">
          <div class="kb-lane-name"><span class="kb-chev" data-action="lane-toggle" data-domain="${escapeHtml(domainKey)}" role="button" title="접기">▾</span> ${name}</div>
          <div class="kb-lane-meta">
            <span class="kb-badge">${rows.length}</span>
            ${active ? `<span class="kb-badge">진행 ${active}</span>` : ''}
            ${urgent ? `<span class="kb-badge urgent">🔴 ${urgent}</span>` : ''}
          </div>
        </div>
        ${cells}
      </div>`;
  }

  private renderCard(r: api.IssueListRow, opts: { showDomain?: boolean; drag?: boolean } = {}): string {
    const drag = opts.drag !== false;
    const dom = opts.showDomain ? `<span class="kb-dom">${escapeHtml(domainLabel(r.domain))}</span>` : '';
    const grp = r.group_id ? `<span class="kb-grp${r.group_fresh ? ' fresh' : ''}" title="묶음 #${r.group_id}">🔗${r.group_size > 1 ? ' ' + r.group_size : ''}</span>` : '';
    const today = new Date().toISOString().slice(0, 10);
    const overdue = !!r.due_date && r.due_date < today && r.status !== 'closed' && r.status !== 'resolved';
    const due = r.due_date ? `<span class="kb-due${overdue ? ' over' : ''}" title="마감 ${escapeHtml(r.due_date)}">📅 ${escapeHtml(r.due_date.slice(5))}</span>` : '';
    const accent = (r.severity && SEVERITY_COLOR[r.severity]) || STATUS_COLOR[r.status] || '#8a93a6';
    const sev = r.severity
      ? `<span class="kb-sev" style="background:${SEVERITY_COLOR[r.severity] ?? '#8a93a6'}1f;color:${SEVERITY_COLOR[r.severity] ?? '#8a93a6'}">${escapeHtml(r.severity)}</span>` : '';
    const cat = r.category ? `<span class="kb-chip">${escapeHtml(r.category)}</span>` : '';
    const sub = r.subdomain ? `<span class="kb-sub">${escapeHtml(subLabel(r.domain, r.subdomain))}</span>` : '';
    const mine = r.is_mine ? `<span title="내 제보">🙋</span>` : '';
    const tags = (r.tags ?? []).slice(0, 3).map((t) => `<span class="kb-tag">#${escapeHtml(t)}</span>`).join('');
    const reporter = r.last_reporter_name
      ? escapeHtml(r.last_reporter_name) + (r.reporter_count > 1 ? ` 외 ${r.reporter_count - 1}` : '') : '—';
    const date = r.last_reported_at ? escapeHtml(fmtDate(r.last_reported_at)) : '—';
    return `
      <div class="kb-card" draggable="${drag}" data-action="card" data-issue-id="${r.id}"
           data-domain="${escapeHtml(r.domain ?? '')}" data-status="${r.status}" style="--accent:${accent}">
        <div class="kb-card-top"><span class="kb-id">#${r.id}</span>${dom}${cat}${sub}${grp}<span class="kb-spacer"></span>${due}${sev}${mine}</div>
        <div class="kb-title">${escapeHtml(r.title)}</div>
        ${tags ? `<div class="kb-tags">${tags}</div>` : ''}
        <div class="kb-meta"><span>제보 ${r.report_count}</span><span class="kb-sep">·</span><span>${reporter}</span><span class="kb-sep">·</span><span>${date}</span></div>
      </div>`;
  }

  // ─── 목적별(업무태그 클러스터) ────────────────────────────────────
  private renderPurpose(): string {
    const rows = this.filtered();
    if (rows.length === 0) return `<div class="kb-purpose"><p class="empty">조건에 맞는 이슈가 없습니다.</p></div>`;
    const byTag = new Map<string, api.IssueListRow[]>();
    const untagged: api.IssueListRow[] = [];
    for (const r of rows) {
      const tags = r.tags ?? [];
      if (tags.length === 0) { untagged.push(r); continue; }
      for (const t of tags) { let a = byTag.get(t); if (!a) { a = []; byTag.set(t, a); } a.push(r); }
    }
    const keys = [...byTag.keys()].sort();
    const sections = keys.map((t) => this.renderTagSection('#' + t, byTag.get(t)!)).join('')
      + (untagged.length ? this.renderTagSection('태그 없음', untagged) : '');
    return `<div class="kb-purpose">${sections}</div>
      <p class="list-footer text-muted">총 ${rows.length} 건 · ${keys.length} 목적${this.data.fMine ? ' · 내 제보' : ''}</p>`;
  }

  private renderTagSection(label: string, rows: api.IssueListRow[]): string {
    const active = rows.filter((r) => r.status === 'open' || r.status === 'in_progress').length;
    const cards = rows.map((r) => this.renderCard(r, { showDomain: true, drag: false })).join('');
    return `
      <section class="kb-tag-section">
        <div class="kb-tag-h">
          <span class="kb-tag-name">${escapeHtml(label)}</span>
          <span class="kb-badge">${rows.length}</span>
          ${active ? `<span class="kb-badge">진행 ${active}</span>` : ''}
        </div>
        <div class="kb-tag-grid">${cards}</div>
      </section>`;
  }

  // ─── 오버레이(토스트·모달·드로어) — .issue-content 밖(rerender 영향 없음) ──
  private renderOverlays(): string {
    return `
      <div class="issue-toast" data-toast>
        <span data-toast-msg></span>
        <button class="issue-toast-undo" type="button" data-action="undo">되돌리기</button>
        <button class="issue-toast-x" type="button" data-action="toast-close">×</button>
      </div>
      <div class="issue-overlay" data-overlay data-action="overlay-click"></div>
      <div class="issue-resmodal" data-resmodal>
        <h3>이슈 닫기 — 종류 선택</h3>
        <div class="res-desc" data-res-desc></div>
        <label class="res-opt"><input type="radio" name="res" value="fixed" data-action="res-pick"><span><b>고쳐짐</b> — 코드로 고침</span></label>
        <label class="res-opt"><input type="radio" name="res" value="rejected" data-action="res-pick"><span><b>거절</b> — 안 하기로 결정 (사유 필수)</span></label>
        <label class="res-opt"><input type="radio" name="res" value="replanned" data-action="res-pick"><span><b>기획 전환</b> — 흐름/정책으로 풂 (사유 필수)</span></label>
        <label class="res-opt"><input type="radio" name="res" value="guided" data-action="res-pick"><span><b>안내</b> — 사용법이었음</span></label>
        <textarea class="res-note" data-action="res-note" rows="2" placeholder="사유 (거절/기획 전환은 필수)"></textarea>
        <div class="res-actions">
          <button class="issue-btn" type="button" data-action="res-cancel">취소</button>
          <button class="issue-btn danger" type="button" data-action="res-submit">닫기 확정</button>
        </div>
      </div>
      <aside class="issue-drawer" data-drawer></aside>`;
  }

  // ─── 필터 핸들러 ────────────────────────────────────────────────────
  handleToggleMine(): void { this.data.fMine = !this.data.fMine; this.rerender(); }
  handleViewBoard(): void { if (this.data.view !== 'board') { this.data.view = 'board'; this.rerender(); } }
  handleViewPurpose(): void { if (this.data.view !== 'purpose') { this.data.view = 'purpose'; this.rerender(); } }
  handleHideDone(): void { this.data.hideDone = !this.data.hideDone; this.rerender(); }
  handleLaneToggle(_e: Event, t: HTMLElement): void {
    const dom = t.closest('[data-domain]')?.getAttribute('data-domain');
    if (dom == null) return;
    const i = this.data.collapsed.indexOf(dom);
    if (i >= 0) this.data.collapsed.splice(i, 1); else this.data.collapsed.push(dom);
    this.rerender();
  }
  async handleDueSet(_e: Event, t: HTMLElement): Promise<void> { await this.applyDue((t as HTMLInputElement).value || null); }
  handleDueClear(): void { void this.applyDue(null); }
  private async applyDue(due: string | null): Promise<void> {
    const id = this.data.drawerId; if (id == null) return;
    try {
      await api.updateDueDate(id, due);
      const list = await api.fetchIssueList(); this.data.rows = list.rows;
      const det = this.data.detail[id]; if (det) det.due_date = due;
      this.rerender(); this.paintDrawer();
      this.showToast(due ? `이슈 #${id} 마감일 → ${due}` : `이슈 #${id} 마감일 해제`, false);
    } catch (err) {
      this.data.actionError = `마감일 변경 실패: ${(err as Error).message}`; this.paintDrawer();
    }
  }
  handleFDomain(_e: Event, t: HTMLElement): void { this.data.fDomain = (t as HTMLSelectElement).value; this.rerender(); }
  handleFSeverity(_e: Event, t: HTMLElement): void { this.data.fSeverity = (t as HTMLSelectElement).value; this.rerender(); }
  handleFStatus(_e: Event, t: HTMLElement): void { this.data.fStatus = (t as HTMLSelectElement).value; this.rerender(); }
  handleFTag(_e: Event, t: HTMLElement): void { this.data.fTag = (t as HTMLSelectElement).value; this.rerender(); }

  // ─── 카드 클릭 → 드로어 ─────────────────────────────────────────────
  async handleCardClick(_e: Event, target: HTMLElement): Promise<void> {
    if (this.dragMoved) { this.dragMoved = false; return; }   // 드래그였으면 무시
    const idAttr = target.closest('[data-issue-id]')?.getAttribute('data-issue-id');
    if (!idAttr) return;
    await this.openDrawer(Number(idAttr));
  }

  private async openDrawer(id: number): Promise<void> {
    this.data.drawerId = id;
    this.data.detailError = '';
    this.paintDrawer();
    if (!this.data.detail[id]) {
      this.data.loadingDetail = id;
      this.paintDrawer();
      try { this.data.detail[id] = await api.fetchIssueDetail(id); }
      catch (err) { this.data.detailError = `상세 로드 실패: ${(err as Error).message}`; }
      finally { this.data.loadingDetail = null; }
    }
    this.paintDrawer();
  }
  handleDrawerClose(): void { this.closeDrawer(); }
  handleOverlayClick(): void {
    if (this.data.closeId != null) { this.closeResolution(); return; }
    if (this.data.drawerId != null) { this.closeDrawer(); }
  }
  private closeDrawer(): void {
    this.data.drawerId = null;
    this._rootElement?.querySelector('[data-drawer]')?.classList.remove('show');
    if (this.data.closeId == null) this._rootElement?.querySelector('[data-overlay]')?.classList.remove('show');
  }

  private paintDrawer(): void {
    const dr = this._rootElement?.querySelector<HTMLElement>('[data-drawer]');
    const ov = this._rootElement?.querySelector<HTMLElement>('[data-overlay]');
    if (!dr) return;
    const id = this.data.drawerId;
    if (id == null) { dr.classList.remove('show'); return; }
    dr.innerHTML = this.renderDrawerInner(id);
    dr.classList.add('show');
    ov?.classList.add('show');
  }

  private renderDrawerInner(id: number): string {
    const row = this.data.rows.find((r) => r.id === id);
    const d = this.data.detail[id];
    const head = `
      <div class="dr-head">
        <div><div class="dr-id">#${id}${row ? ' · ' + escapeHtml(domainLabel(row.domain))
          + (row.subdomain ? ' › ' + escapeHtml(subLabel(row.domain, row.subdomain)) : '') : ''}</div>
          <h2>${escapeHtml(row?.title ?? d?.title ?? '')}</h2></div>
        <button class="dr-x" type="button" data-action="drawer-close" aria-label="닫기">×</button>
      </div>`;
    if (this.data.loadingDetail === id || !d) {
      const err = this.data.detailError ? `<p class="banner-error">${escapeHtml(this.data.detailError)}</p>` : `<p class="text-muted">상세 불러오는 중…</p>`;
      return head + `<div class="dr-body">${err}</div>`;
    }
    return head + `<div class="dr-body">
      <div class="dr-controls">${this.renderStatusPick(d)}${this.renderSeverityPick(d)}${this.renderDuePick(d)}</div>
      ${this.data.actionError && this.data.drawerId === id ? `<p class="banner-error">${escapeHtml(this.data.actionError)}</p>` : ''}
      ${this.renderGroup(d)}
      ${d.summary ? `<div class="dr-summary">${escapeHtml(d.summary)}</div>` : ''}
      ${this.renderPlanning(d)}
      ${this.data.isDeveloper ? this.renderDev(d) : ''}
      ${this.renderReports(d)}
      ${this.renderEvents(d)}
    </div>`;
  }

  private renderGroup(d: api.IssueDetail): string {
    if (!d.group_id) return '';
    const total = (d.group_members?.length ?? 0) + 1;
    const fresh = d.group_fresh ? '<span class="grp-dot" title="최근 편성(24h)"></span>' : '';
    const members = (d.group_members ?? []).map((m) =>
      `<button class="grp-item" type="button" data-action="card" data-issue-id="${m.id}"><span class="grp-mid">#${m.id}</span> ${escapeHtml(m.title)}</button>`).join('');
    return `<div class="dr-group">
      <div class="dr-sec-h">🔗 같은 묶음 <span class="grp-cnt">${total}</span>${fresh} <span class="text-muted" style="font-weight:400">· 코드 근접 자동</span></div>
      <div class="grp-list">${members || '<span class="text-muted" style="font-size:.82rem">이 이슈만</span>'}</div>
    </div>`;
  }

  private renderPlanning(d: api.IssueDetail): string {
    const rows: string[] = [];
    const f = (k: string, v: string | null) => { if (v && v.trim()) rows.push(`<div class="pl-row"><div class="pl-k">${k}</div><div class="pl-v">${escapeHtml(v)}</div></div>`); };
    f('목적', d.purpose); f('목표', d.goal); f('타겟', d.target); f('필수내용', d.must_have);
    if (rows.length === 0) return '';
    return `<div class="dr-plan"><div class="dr-sec-h">📐 기획 정리</div>${rows.join('')}</div>`;
  }

  private renderDev(d: api.IssueDetail): string {
    const canEnrich = this.canTransition();
    const enriching = this.data.enrichingId === d.id;
    const hints = d.code_hints ?? [];
    const btn = canEnrich ? `<button class="issue-tbtn dev-btn" type="button" data-action="enrich"${enriching ? ' disabled' : ''}>${enriching ? '분석 중…' : '코드 재분석'}</button>` : '';
    const hintsHtml = hints.length
      ? `<ul class="dev-hints">${hints.map((h) => `<li><code>${escapeHtml(h.file)}${h.line ? ':' + h.line : ''}</code>${h.match ? ` <span class="text-muted">${escapeHtml(h.match)}</span>` : ''}</li>`).join('')}</ul>` : '';
    const body = (d.dev_note || hints.length)
      ? `${d.dev_note ? `<div class="dev-note">${escapeHtml(d.dev_note)}</div>` : ''}${hintsHtml}`
      : `<div class="text-muted" style="font-size:.82rem">아직 코드 분석 없음.${canEnrich ? ' [코드 재분석]으로 생성.' : ''}</div>`;
    return `<div class="dr-dev"><div class="dr-sec-h">🛠 개발자 참고 <span class="text-muted">(코드 분석 · 추정)</span>${btn}</div>${body}</div>`;
  }

  private renderReports(d: api.IssueDetail): string {
    const items = d.reports.map((rep) => {
      const who = rep.reporter_name ?? rep.slack_user_id ?? '알 수 없음';
      const re = rep.kind === 'reoccur' ? ' <span style="color:#dc2626;font-size:.72rem">재발</span>' : '';
      const ex = (rep.extracted ?? {}) as { error_text?: string; followup_answer?: string; images?: Array<{ url: string; name: string }> };
      const parts: string[] = [];
      if (ex.images && ex.images.length) {
        parts.push(`<div class="dr-shots">${ex.images.map((im) => `<a href="${escapeHtml(im.url)}" target="_blank" rel="noopener"><img class="dr-shot" src="${escapeHtml(im.url)}" alt="${escapeHtml(im.name)}" loading="lazy"></a>`).join('')}</div>`);
      }
      if (ex.error_text) parts.push(`<div class="dr-ex"><b>화면 오류</b> ${escapeHtml(ex.error_text)}</div>`);
      if (ex.followup_answer) parts.push(`<div class="dr-ex"><b>되물음 답</b> ${escapeHtml(ex.followup_answer)}</div>`);
      return `<li class="dr-rep"><div class="dr-who">${escapeHtml(who)} · ${escapeHtml(rep.origin)} · ${escapeHtml(fmtDateTime(rep.created_at))}${re}</div><div class="dr-raw">${escapeHtml(rep.raw_text ?? '')}</div>${parts.join('')}</li>`;
    }).join('');
    return `<div class="dr-block"><div class="dr-sec-h">제보 ${d.reports.length}건</div><ul class="dr-list">${items}</ul></div>`;
  }
  private renderEvents(d: api.IssueDetail): string {
    const items = d.events.map((ev) => {
      const label = ev.to_status ? `${escapeHtml(ev.kind)} → ${escapeHtml(STATUS_LABEL[ev.to_status as api.IssueStatus] ?? ev.to_status)}` : escapeHtml(ev.kind);
      const who = ev.actor_name ? ` · ${escapeHtml(ev.actor_name)}` : (ev.actor === 'bot' ? ' · 봇' : '');
      return `<li class="dr-ev">${escapeHtml(fmtDateTime(ev.created_at))} · ${label}${who}${ev.message ? ' · ' + escapeHtml(ev.message) : ''}</li>`;
    }).join('');
    return `<div class="dr-block"><div class="dr-sec-h">이력</div><ul class="dr-list">${items}</ul></div>`;
  }

  // ─── 상태 pill 피커(드로어 안 — 정밀 전이) ───────────────────────────
  private canTransition(): boolean { const r = getCurrentRole(); return r === 'owner' || r === 'leader'; }
  private renderDuePick(d: api.IssueDetail): string {
    if (!this.canTransition()) {
      return d.due_date ? `<span class="due-pill readonly">📅 ${escapeHtml(d.due_date)}</span>` : '';
    }
    return `<label class="due-pill"><span class="due-ico">📅</span><input type="date" class="due-input" data-action="due-set" value="${d.due_date ?? ''}">${d.due_date ? '<button type="button" class="due-clear" data-action="due-clear" title="마감일 해제">×</button>' : ''}</label>`;
  }
  private renderStatusPick(d: api.IssueDetail): string {
    const st = { k: d.status, label: STATUS_LABEL[d.status], color: STATUS_COLOR[d.status] };
    if (!this.canTransition()) {
      return `<div class="dr-status"><span class="st-pill readonly"><span class="st-dot" style="background:${st.color}"></span>${st.label}</span></div>`;
    }
    const targets = ALLOWED[d.status] ?? [];
    const opts = targets.map((to) => {
      const danger = to === 'closed';
      const lbl = danger ? '닫기…' : (to === 'open' && d.status !== 'closed' ? '→ 되돌리기 (대기)' : (to === 'open' ? '→ 다시 열기' : '→ ' + STATUS_LABEL[to]));
      return `<button class="st-item${danger ? ' danger' : ''}" type="button" data-action="pick-to" data-to="${to}"><span class="st-dot" style="background:${STATUS_COLOR[to]}"></span>${lbl}</button>`;
    }).join('');
    const busy = this.data.transitioning ? ' <span class="text-muted" style="font-size:.8rem">처리 중…</span>' : '';
    return `<div class="dr-status">
      <div class="st-pick">
        <button class="st-pill" type="button" data-action="pick-open"><span class="st-dot" style="background:${st.color}"></span>${st.label}<span class="st-caret">▾</span></button>
        <div class="st-menu" data-st-menu><div class="st-mlabel">상태 변경</div>${opts}</div>
      </div>${busy}
    </div>`;
  }
  private renderSeverityPick(d: api.IssueDetail): string {
    const sv = d.severity ?? '보통';
    const color = SEVERITY_COLOR[sv] ?? '#8a93a6';
    if (!this.canTransition()) {
      return `<span class="sev-pill readonly"><span class="sev-dot" style="background:${color}"></span>${escapeHtml(sv)}</span>`;
    }
    const opts = SEVERITY_LIST.map((s) =>
      `<button class="sev-item" type="button" data-action="sev-to" data-sev="${escapeHtml(s)}"><span class="sev-dot" style="background:${SEVERITY_COLOR[s] ?? '#8a93a6'}"></span>${escapeHtml(s)}</button>`).join('');
    return `<div class="sev-pick">
      <button class="sev-pill" type="button" data-action="sev-open"><span class="sev-dot" style="background:${color}"></span>${escapeHtml(sv)}<span class="st-caret">▾</span></button>
      <div class="sev-menu" data-sev-menu><div class="st-mlabel">심각도</div>${opts}</div>
    </div>`;
  }

  handlePickOpen(_e: Event, t: HTMLElement): void {
    const menu = t.parentElement?.querySelector('[data-st-menu]');
    menu?.classList.toggle('show');
  }
  handlePickTo(_e: Event, t: HTMLElement): void {
    const to = t.getAttribute('data-to') as api.IssueStatus | null;
    const id = this.data.drawerId;
    t.closest('[data-st-menu]')?.classList.remove('show');
    if (!to || id == null) return;
    if (to === 'closed') { this.openResolution(id); return; }
    void this.doTransition(id, { to });
  }
  handleSevOpen(_e: Event, t: HTMLElement): void {
    t.parentElement?.querySelector('[data-sev-menu]')?.classList.toggle('show');
  }
  async handleSevTo(_e: Event, t: HTMLElement): Promise<void> {
    const sev = t.getAttribute('data-sev');
    const id = this.data.drawerId;
    t.closest('[data-sev-menu]')?.classList.remove('show');
    if (!sev || id == null) return;
    try {
      await api.updateSeverity(id, sev);
      const list = await api.fetchIssueList();
      this.data.rows = list.rows;
      const det = this.data.detail[id]; if (det) det.severity = sev;
      this.rerender(); this.paintDrawer();
      this.showToast(`이슈 #${id} 심각도 → ${sev}`, false);
    } catch (err) {
      this.data.actionError = `심각도 변경 실패: ${(err as Error).message}`;
      this.paintDrawer();
    }
  }

  // ─── resolution 모달 ────────────────────────────────────────────────
  private openResolution(id: number): void {
    this.data.closeId = id; this.data.closeResolution = ''; this.data.closeNote = ''; this.data.actionError = '';
    const m = this._rootElement;
    m?.querySelectorAll<HTMLInputElement>('[data-resmodal] input[name="res"]').forEach((i) => { i.checked = false; });
    const note = m?.querySelector<HTMLTextAreaElement>('[data-resmodal] .res-note'); if (note) note.value = '';
    const desc = m?.querySelector<HTMLElement>('[data-res-desc]');
    const row = this.data.rows.find((r) => r.id === id);
    if (desc) desc.textContent = `#${id} · ${row?.title ?? ''}`;
    m?.querySelector('[data-overlay]')?.classList.add('show');
    m?.querySelector('[data-resmodal]')?.classList.add('show');
  }
  handleResPick(_e: Event, t: HTMLElement): void { this.data.closeResolution = (t as HTMLInputElement).value; }
  handleResNote(_e: Event, t: HTMLElement): void { this.data.closeNote = (t as HTMLTextAreaElement).value; }
  handleResCancel(): void { this.closeResolution(); }
  private closeResolution(): void {
    this.data.closeId = null;
    this._rootElement?.querySelector('[data-resmodal]')?.classList.remove('show');
    if (this.data.drawerId == null) this._rootElement?.querySelector('[data-overlay]')?.classList.remove('show');
  }
  handleResSubmit(): void {
    const id = this.data.closeId; if (id == null) return;
    const sel = this.data.closeResolution;
    if (!sel) { window.alert('닫기 종류를 선택하세요.'); return; }
    if ((sel === 'rejected' || sel === 'replanned') && !this.data.closeNote.trim()) { window.alert('거절/기획 전환은 사유가 필요합니다.'); return; }
    this.closeResolution();
    void this.doTransition(id, { to: 'closed', resolution: sel as api.IssueResolution, note: this.data.closeNote || undefined });
  }

  // ─── 전이 실행 ──────────────────────────────────────────────────────
  private async doTransition(id: number, body: api.TransitionBody, isUndo = false): Promise<void> {
    const cur = this.data.rows.find((r) => r.id === id);
    const from = cur?.status ?? null;
    this.data.transitioning = true; this.data.actionError = '';
    this.paintDrawer();
    try {
      await api.transitionIssue(id, body);
      const list = await api.fetchIssueList();
      this.data.rows = list.rows;
      delete this.data.detail[id];
      if (this.data.drawerId === id) this.data.detail[id] = await api.fetchIssueDetail(id);
      if (!isUndo && from) this.data.lastUndo = { id, from };
      this.rerender();
      this.paintDrawer();
      const label = body.to === 'closed' ? '닫힘' : (STATUS_LABEL[body.to] ?? body.to);
      this.showToast(`이슈 #${id} → ${label}`, !isUndo);
    } catch (err) {
      this.data.actionError = `전이 실패: ${(err as Error).message}`;
      this.paintDrawer();
    } finally {
      this.data.transitioning = false;
    }
  }

  // ─── 토스트 ─────────────────────────────────────────────────────────
  private showToast(msg: string, withUndo: boolean): void {
    const t = this._rootElement?.querySelector<HTMLElement>('[data-toast]');
    const m = this._rootElement?.querySelector<HTMLElement>('[data-toast-msg]');
    const u = this._rootElement?.querySelector<HTMLElement>('.issue-toast-undo');
    if (!t || !m) return;
    m.textContent = msg;
    if (u) u.style.display = withUndo ? '' : 'none';
    t.classList.add('show');
    if (this.toastTimer) clearTimeout(this.toastTimer);
    this.toastTimer = setTimeout(() => this.hideToast(), 6000);
  }
  private hideToast(): void { this._rootElement?.querySelector('[data-toast]')?.classList.remove('show'); }
  handleToastClose(): void { this.hideToast(); }
  handleUndo(): void {
    const u = this.data.lastUndo; if (!u) return;
    this.data.lastUndo = null; this.hideToast();
    void this.doTransition(u.id, { to: u.from }, true);
  }

  // ─── enrich ─────────────────────────────────────────────────────────
  async handleEnrich(): Promise<void> {
    const id = this.data.drawerId; if (id == null) return;
    this.data.enrichingId = id; this.data.actionError = ''; this.paintDrawer();
    try {
      const res = await api.enrichIssue(id);
      const det = this.data.detail[id];
      if (det) { det.dev_note = res.dev_note; det.code_hints = res.code_hints; }
    } catch (err) { this.data.actionError = `재분석 실패: ${(err as Error).message}`; }
    finally { this.data.enrichingId = null; this.paintDrawer(); }
  }

  // ─── 드래그앤드롭 ──────────────────────────────────────────────────
  private wireDnd(): void {
    const root = this._rootElement; if (!root) return;
    root.querySelectorAll<HTMLElement>('.kb-card').forEach((el) => {
      el.addEventListener('dragstart', (e) => {
        this.dragId = Number(el.dataset.issueId); this.dragMoved = true;
        el.classList.add('dragging');
        (e as DragEvent).dataTransfer!.effectAllowed = 'move';
        const from = el.dataset.status as api.IssueStatus;
        const dom = el.dataset.domain ?? '';
        const allow = ALLOWED[from] ?? [];
        root.querySelectorAll<HTMLElement>('.kb-cell').forEach((c) => {
          if ((c.dataset.domain ?? '') === dom && allow.includes(c.dataset.status as api.IssueStatus)) c.classList.add('drop-ok');
          else if ((c.dataset.domain ?? '') !== dom) c.classList.add('drop-dim');
        });
      });
      el.addEventListener('dragend', () => { el.classList.remove('dragging'); this.clearDrop(); this.dragId = null; setTimeout(() => { this.dragMoved = false; }, 0); });
    });
    root.querySelectorAll<HTMLElement>('.kb-cell').forEach((c) => {
      c.addEventListener('dragover', (e) => { if (c.classList.contains('drop-ok')) e.preventDefault(); });
      c.addEventListener('drop', (e) => {
        e.preventDefault();
        if (!c.classList.contains('drop-ok')) return;
        const id = this.dragId; const to = c.dataset.status as api.IssueStatus;
        this.clearDrop();
        if (id == null) return;
        if (to === 'closed') this.openResolution(id);
        else void this.doTransition(id, { to });
      });
    });
  }
  private clearDrop(): void {
    this._rootElement?.querySelectorAll('.kb-cell').forEach((c) => c.classList.remove('drop-ok', 'drop-dim'));
  }

  // ─── rerender — .issue-content 만 교체 후 DnD 재배선 ─────────────────
  private rerender(): void {
    const el = this._rootElement?.querySelector('.issue-content');
    if (el) { el.innerHTML = this.renderContent(); if (this.data.view === 'board') this.wireDnd(); }
  }
}
